/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import SectionHeader from 'src/components/ts-components/SectionHeader'

interface ApiAccessCardProps {
  title: string
  subtitle: string
  /** The card's header actions: compact Buttons, at most one primary. */
  action?: ReactNode
  /**
   * Keep the header in place and scroll the body inside the card, for a card
   * held to a fixed height by its parent (a flex column with a max height).
   */
  scrollable?: boolean
  children: ReactNode
}

/** A card on the API Access or Website Plugin tab, in the account settings card shell. */
export const ApiAccessCard = ({
  title,
  subtitle,
  action,
  scrollable = false,
  children,
}: ApiAccessCardProps) => (
  <section css={[cardStyle, scrollable && scrollableCardStyle]}>
    <div css={headerStyle}>
      <SectionHeader title={title} subtitle={subtitle} />
      {action ? <div css={actionsStyle}>{action}</div> : null}
    </div>
    {scrollable ? <div css={scrollBodyStyle}>{children}</div> : children}
  </section>
)

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px auto 0;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.SOFT_STEEL};
  background-color: ${colors.HEADER};
`

/** Shrinks to its parent's height, never past its content; stretches full width. */
const scrollableCardStyle = css`
  flex: 0 1 auto;
  min-height: 0;
  margin-inline: 0;
  align-self: stretch;
`

const scrollBodyStyle = css`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const actionsStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`
